import React, { createContext, useContext, useState, useCallback } from 'react';

interface YearContextType {
  selectedYear: number;
  setSelectedYear: (year: number) => void;
  yearOptions: number[];
}

const YearContext = createContext<YearContextType | undefined>(undefined);

export const useYear = () => {
  const context = useContext(YearContext);
  if (context === undefined) {
    throw new Error('useYear must be used within a YearProvider');
  }
  return context;
};

interface YearProviderProps {
  children: React.ReactNode;
}

export const YearProvider = ({ children }: YearProviderProps) => {
  const currentYear = new Date().getFullYear();
  
  // Initialize from localStorage or default to current year
  const [selectedYear, setSelectedYearState] = useState<number>(() => {
    const saved = localStorage.getItem('selectedYear');
    if (saved) {
      const parsed = parseInt(saved, 10);
      if (!isNaN(parsed) && parsed >= 2020 && parsed <= currentYear + 1) {
        return parsed;
      }
    }
    return currentYear;
  });

  // Generate year options (current year + 1 forward to 5 years back)
  const yearOptions = Array.from({ length: 7 }, (_, i) => currentYear + 1 - i);

  const setSelectedYear = useCallback((year: number) => {
    setSelectedYearState(year);
    localStorage.setItem('selectedYear', year.toString());
    
    // Dispatch event so non-React code can react to year changes
    window.dispatchEvent(new CustomEvent('yearChanged', { detail: { year } }));
  }, []);

  return (
    <YearContext.Provider value={{ selectedYear, setSelectedYear, yearOptions }}>
      {children}
    </YearContext.Provider>
  );
};
